/**
 * @namespace WimUtils
 * @description Global utilities shared across WIM sections and Alpine components.
 */
window.WimUtils = window.WimUtils || {};

/**
 * Calculates the recommended kilograms of coffee for a subscription period.
 *
 * The result is rounded up to the nearest 0.5 kg to ensure the customer
 * never runs short of coffee during the selected period.
 *
 * @param {number} cups        - Number of cups consumed per day.
 * @param {number} gramsPerCup - Grams of coffee per cup (e.g. 8.33).
 * @param {'mensual'|'trimestral'} plan - Subscription billing period.
 * @returns {number} Recommended kilograms, rounded up to the nearest 0.5 kg.
 *
 * @example
 * WimUtils.recommendedKg(2, 8.33, 'mensual');    // → 0.5
 * WimUtils.recommendedKg(4, 8.33, 'trimestral'); // → 3
 */
window.WimUtils.recommendedKg = function (cups, gramsPerCup, plan) {
  const months = plan === 'mensual' ? 1 : 3;
  const grams = cups * gramsPerCup * 30 * months;
  return Math.ceil(grams / 500) * 0.5;
};

/**
 * Rounds a grams value up to the nearest 0.5 kg so the customer never runs short.
 *
 * @param {number} grams
 * @returns {number} Kilograms, rounded up to the nearest 0.5 kg.
 */
window.WimUtils.ceilToHalfKg = function (grams) {
  return Math.ceil((Number(grams) || 0) / 500) * 0.5;
};

/**
 * Returns the number of days used for the consumption calculation for a given
 * period and context.
 *
 * - Home: 30 / 90 / 180 calendar days.
 * - Office: 22 / 66 / 132 working days.
 *
 * @param {'mensual'|'trimestral'|'semestral'} period
 * @param {'home'|'office'} [context='home']
 * @returns {number} Days in the period.
 */
window.WimUtils.periodToDays = function (period, context) {
  const ctx = context === 'office' ? 'office' : 'home';
  const table = {
    mensual:    { home: 30,  office: 22 },
    trimestral: { home: 90,  office: 66 },
    semestral:  { home: 180, office: 132 }
  };
  return (table[period] || table.mensual)[ctx];
};

/**
 * Computes the recommended kilograms of coffee for a period, given the user's
 * daily habits. Each extra "double" counts as an additional dose on top of the
 * regular cups.
 *
 * Formula: totalGrams = (cups + doubles) * days * gramsPerCup
 *
 * @param {number} cups        - Regular cups per day.
 * @param {number} doubles     - Extra double doses per day.
 * @param {number} gramsPerCup - Grams of coffee per cup (e.g. 8.33).
 * @param {number} days        - Number of days in the target period.
 * @returns {number} Recommended kilograms, rounded up to the nearest 0.5 kg.
 */
window.WimUtils.recommendedKgFromHabits = function (cups, doubles, gramsPerCup, days) {
  const totalCups = (Number(cups) || 0) + (Number(doubles) || 0);
  const grams = totalCups * (Number(gramsPerCup) || 0) * (Number(days) || 0);
  return window.WimUtils.ceilToHalfKg(grams);
};

/**
 * Formats a kilograms value using Spanish locale (comma decimal separator) and
 * trims the trailing ",0" for whole-kilogram values.
 *
 * @param {number} kg
 * @returns {string} e.g. 2.5 → "2,5", 3 → "3"
 */
window.WimUtils.formatKg = function (kg) {
  const n = Number(kg) || 0;
  return (Number.isInteger(n) ? String(n) : n.toFixed(1)).replace('.', ',');
};

/**
 * Formats a price in cents to a Spanish-locale EUR string.
 *
 * @param {number} cents
 * @returns {string} e.g. 1299 → "12,99 €"
 */
window.WimUtils.formatMoney = function (cents) {
  const n = (Number(cents) || 0) / 100;
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
};

/**
 * Finds the cheapest subscription bag combination that covers at least targetKg,
 * using unbounded knapsack DP. Tiebreaker on equal price: fewer bags.
 *
 * @param {number} targetKg
 * @param {Array<{id:number, title:string, grams:number, priceOnetime:number, priceSubscription:number}>} variants
 * @returns {{ items: Object.<number,number>, totalGrams: number, subPrice: number, onetimePrice: number, bags: number }|null}
 */
window.WimUtils.bestBagCombo = function (targetKg, variants) {
  const targetG = Math.round(targetKg * 1000);
  if (targetG <= 0 || !variants || variants.length === 0) return null;

  const maxVar = variants.reduce(function (m, v) { return Math.max(m, v.grams | 0); }, 0);
  if (maxVar === 0) return null;
  const limit  = targetG + maxVar;
  const INF    = Infinity;

  const dpSub  = new Array(limit + 1).fill(INF);
  const dpOt   = new Array(limit + 1).fill(INF);
  const dpBags = new Array(limit + 1).fill(INF);
  const dpFrom = new Array(limit + 1).fill(null);

  dpSub[0] = 0; dpOt[0] = 0; dpBags[0] = 0;

  for (let g = 0; g < limit; g++) {
    if (dpSub[g] === INF) continue;
    for (let vi = 0; vi < variants.length; vi++) {
      const v  = variants[vi];
      const ng = g + (v.grams | 0);
      if (ng > limit) continue;
      const ns = dpSub[g]  + v.priceSubscription;
      const no = dpOt[g]   + v.priceOnetime;
      const nb = dpBags[g] + 1;
      if (ns < dpSub[ng] || (ns === dpSub[ng] && nb < dpBags[ng])) {
        dpSub[ng] = ns; dpOt[ng] = no; dpBags[ng] = nb;
        dpFrom[ng] = { g: g, id: v.id };
      }
    }
  }

  let bestG = -1;
  for (let g = targetG; g <= limit; g++) {
    if (dpSub[g] === INF) continue;
    if (bestG < 0 || dpSub[g] < dpSub[bestG] ||
        (dpSub[g] === dpSub[bestG] && dpBags[g] < dpBags[bestG])) {
      bestG = g;
    }
  }
  if (bestG < 0) return null;

  const items = {};
  let cur = bestG;
  while (dpFrom[cur] !== null) {
    const f = dpFrom[cur];
    items[f.id] = (items[f.id] || 0) + 1;
    cur = f.g;
  }

  return { items: items, totalGrams: bestG, subPrice: dpSub[bestG], onetimePrice: dpOt[bestG], bags: dpBags[bestG] };
};

/**
 * Alpine store: wimModal
 * Global state for the WIM side-panel modal triggered by wim-buttons-per-tag.
 * Usage: $store.wimModal.show('coffee-needed') / $store.wimModal.hide()
 */
document.addEventListener('alpine:init', () => {
  Alpine.store('wimModal', {
    open: false,
    type: null,

    /**
     * @param {'coffee-needed'|'calculate-savings'|'help'} type
     */
    show(type) {
      this.type = type;
      this.open = true;
    },

    hide() {
      this.open = false;
      this.type = null;
    }
  });

  /**
   * Alpine component: wimCalculator
   * Shared state for the coffee-needed calculator modal. All slices bind to
   * this single x-data so inputs, period and results stay in sync.
   *
   * The root element may expose `data-grams-per-cup` and `data-context`
   * attributes to override the defaults.
   */
  Alpine.data('wimCalculator', () => ({
    cups: 1,
    doubles: 0,
    doublesOpen: false,
    period: 'trimestral',
    purchaseType: 'subscription',
    context: 'home',
    gramsPerCup: 8.33,
    availablePeriods: [],
    variants: [],
    adding: false,

    init() {
      const g = parseFloat(this.$el.dataset.gramsPerCup);
      if (Number.isFinite(g) && g > 0) this.gramsPerCup = g;
      const c = this.$el.dataset.context;
      if (c === 'home' || c === 'office') this.context = c;
      try {
        const raw = this.$el.dataset.periods;
        if (raw) this.availablePeriods = JSON.parse(raw);
      } catch (_) {}
      if (this.availablePeriods.length > 0 && !this.availablePeriods.includes(this.period)) {
        this.period = this.availablePeriods[0];
      }
      try {
        const rv = this.$el.dataset.variants;
        if (rv) this.variants = JSON.parse(rv);
      } catch (_) {}
    },

    incCups() { this.cups += 1; },
    decCups() { if (this.cups > 1) this.cups -= 1; },
    incDoubles() { this.doubles += 1; },
    decDoubles() { if (this.doubles > 0) this.doubles -= 1; },
    toggleDoubles() { this.doublesOpen = !this.doublesOpen; },

    get periodDays() {
      return window.WimUtils.periodToDays(this.period, this.context);
    },
    get recommendedKg() {
      return window.WimUtils.recommendedKgFromHabits(
        this.cups, this.doubles, this.gramsPerCup, this.periodDays
      );
    },
    get recommendedKgLabel() {
      return window.WimUtils.formatKg(this.recommendedKg);
    },
    get totalCupsForPeriod() {
      const daily = (Number(this.cups) || 0) + (Number(this.doubles) || 0);
      return daily * this.periodDays;
    },

    get variantsForPeriod() {
      const p = this.period;
      return this.variants
        .filter(function (v) { return v.plans && v.plans[p] && v.grams > 0; })
        .map(function (v) {
          return {
            id: v.id, title: v.title, grams: v.grams,
            priceOnetime: v.priceOnetime, priceSubscription: v.plans[p].price
          };
        });
    },

    get bestCombo() {
      return window.WimUtils.bestBagCombo(this.recommendedKg, this.variantsForPeriod);
    },

    get bestComboLabel() {
      const combo = this.bestCombo;
      if (!combo) return '';
      const vMap = {};
      this.variantsForPeriod.forEach(function (v) { vMap[v.id] = v.title; });
      return Object.entries(combo.items)
        .map(function (kv) { return kv[1] + ' × ' + (vMap[kv[0]] || kv[0]); })
        .join(' + ');
    },

    get bestComboLines() {
      const combo = this.bestCombo;
      if (!combo) return [];
      const vMap = {};
      this.variantsForPeriod.forEach(function (v) { vMap[v.id] = v.title; });
      return Object.entries(combo.items).map(function (kv) {
        return { id: kv[0], qty: kv[1], title: vMap[kv[0]] || kv[0] };
      });
    },

    get subPriceLabel() {
      const c = this.bestCombo;
      return c ? window.WimUtils.formatMoney(c.subPrice) : '';
    },

    get onetimePriceLabel() {
      const c = this.bestCombo;
      return c ? window.WimUtils.formatMoney(c.onetimePrice) : '';
    },

    get savingsPct() {
      const c = this.bestCombo;
      if (!c || !c.onetimePrice) return 0;
      return Math.round((c.onetimePrice - c.subPrice) / c.onetimePrice * 100);
    },

    get savingsAmountLabel() {
      const c = this.bestCombo;
      if (!c) return '';
      const diff = Math.max(0, c.onetimePrice - c.subPrice);
      return window.WimUtils.formatMoney(diff);
    },

    async addToCart() {
      const combo = this.bestCombo;
      if (!combo || this.adding) return;
      const p = this.period;
      const planMap = {};
      this.variants.forEach(function (v) {
        if (v.plans && v.plans[p]) planMap[v.id] = v.plans[p].planId;
      });
      const isSub = this.purchaseType === 'subscription';
      const items = Object.entries(combo.items).map(function (kv) {
        const item = { id: Number(kv[0]), quantity: kv[1] };
        if (isSub) {
          const planId = planMap[kv[0]];
          if (planId) item.selling_plan = planId;
        }
        return item;
      });

      const cartHelper = window.Alpine && Alpine.store('xCartHelper');
      const sectionsToRender = cartHelper && typeof cartHelper.getSectionsToRender === 'function'
        ? cartHelper.getSectionsToRender()
        : [];
      const sectionIds = sectionsToRender.map(function (s) { return s.id; });

      this.adding = true;
      try {
        const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items, sections: sectionIds, sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (data && (data.status === 422 || data.status === '422' || data.status === 400)) {
          console.error('[WimCalculator] addToCart rejected:', data.description || data.message);
          return;
        }

        if (data && data.sections) {
          // theme.js declara getSectionInnerHTML con const: es global pero no cuelga de window.
          const sectionHTML = typeof getSectionInnerHTML === 'function' ? getSectionInnerHTML : window.getSectionInnerHTML;
          sectionsToRender.forEach(function (section) {
            const el = document.querySelector(section.selector);
            if (el && data.sections[section.id] && typeof sectionHTML === 'function') {
              el.innerHTML = sectionHTML(data.sections[section.id], section.selector);
            }
          });
        }
        const bubble = document.querySelector('#cart-icon-bubble span');
        if (bubble && cartHelper) cartHelper.currentItemCount = parseInt(bubble.innerHTML);

        document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
        if (window.Alpine && Alpine.store('xMiniCart')) {
          if (typeof Alpine.store('xMiniCart').reLoad === 'function') Alpine.store('xMiniCart').reLoad();
          Alpine.store('xMiniCart').openCart();
        }
        Alpine.store('wimModal').hide();
      } catch (e) {
        console.error('[WimCalculator] addToCart:', e);
      } finally {
        this.adding = false;
      }
    }
  }));
});
